import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError('L\'email è obbligatoria');
      return;
    }

    if (!firstName.trim()) {
      setError('Il nome è obbligatorio');
      return;
    }

    if (!lastName.trim()) {
      setError('Il cognome è obbligatorio');
      return;
    }

    if (!activationCode.trim()) {
      setError('Il codice di attivazione è obbligatorio');
      return;
    }

    setIsLoading(true);

    try {
      await register(email, firstName, lastName, phoneNumber, activationCode);
      navigate('/device-info');
    } catch (err: any) {
      // Gestione specifica degli errori
      if (err.response?.status === 403) {
        setError(err.response?.data?.message || 'Accesso negato: utente bloccato');
      } else if (err.response?.status === 409) {
        setError('Un utente con questa email esiste già');
      } else if (err.response?.status === 401) {
        // Gestione specifica per errori 401 (codice non valido, inattivo, scaduto)
        const message = err.response?.data?.message || 'Credenziali non valide o codice di attivazione errato';
        if (message.includes('non attivo')) {
          setError('Codice di attivazione non attivo. Contatta l\'amministratore.');
        } else if (message.includes('scaduto')) {
          setError('Codice di attivazione scaduto. Richiedi un nuovo codice.');
        } else if (message.includes('non valido')) {
          setError('Codice di attivazione non valido. Verifica il codice inserito.');
        } else if (message.includes('già utilizzato da un altro utente')) {
          setError('Codice di attivazione già utilizzato da un altro utente. Contatta l\'amministratore.');
        } else if (message.includes('Limite dispositivi raggiunto')) {
          setError('Limite dispositivi raggiunto per questo codice. Contatta l\'amministratore.');
        } else {
          setError(message);
        }
      } else if (err.response?.status === 400) {
        // Gestione specifica per errori 400 (codice inattivo, scaduto, ecc.)
        const message = err.response?.data?.message || 'Errore con il codice di attivazione';
        if (message.includes('non attivo')) {
          setError('Codice di attivazione non attivo. Contatta l\'amministratore.');
        } else if (message.includes('scaduto')) {
          setError('Codice di attivazione scaduto. Richiedi un nuovo codice.');
        } else if (message.includes('Limite dispositivi')) {
          setError('Limite dispositivi raggiunto per questo codice.');
        } else if (message.includes('Limite tentativi')) {
          setError('Limite tentativi raggiunto per questo codice.');
        } else {
          setError(message);
        }
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Errore durante la registrazione. Riprova più tardi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/logo-live-concorsi.png" 
              alt="Live Concorsi" 
              className="h-12 w-auto"
            />
            <span className="text-2xl font-bold text-gray-900">Live Concorsi</span>
          </Link>
        </div>

        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Attiva il tuo account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Inserisci la tua email e il codice di attivazione per iniziare
        </p>
        <p className="mt-1 text-center text-sm text-gray-600">
          Hai già un account?{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Accedi qui
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Indirizzo email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="inserisci@email.com"
                />
              </div>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                Nome
              </label>
              <div className="mt-1">
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input-field"
                  placeholder="Mario"
                />
              </div>
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Cognome
              </label>
              <div className="mt-1">
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="input-field"
                  placeholder="Rossi"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                Numero di telefono <span className="text-gray-500">(opzionale)</span>
              </label>
              <div className="mt-1">
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  autoComplete="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="input-field"
                  placeholder="+39 123 456 7890"
                />
              </div>
            </div>

            {/* Activation Code */}
            <div>
              <label htmlFor="activationCode" className="block text-sm font-medium text-gray-700">
                Codice di Attivazione
              </label>
              <div className="mt-1">
                <input
                  id="activationCode"
                  name="activationCode"
                  type="text"
                  required
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                  className="input-field font-mono"
                  placeholder="CODICEXXX"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Inserisci il codice di attivazione fornito
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className={`rounded-lg p-4 ${
                error.includes('bloccato') || error.includes('Accesso negato')
                  ? 'bg-red-50 border border-red-200'
                  : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite')
                  ? 'bg-orange-50 border border-orange-200'
                  : 'bg-danger-50 border border-danger-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {error.includes('bloccato') || error.includes('Accesso negato') ? (
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite') ? (
                      <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-danger-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      error.includes('bloccato') || error.includes('Accesso negato')
                        ? 'text-red-800'
                        : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite')
                        ? 'text-orange-800'
                        : 'text-danger-700'
                    }`}>
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Attivazione in corso...' : 'Attiva Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
